/**
 * Origin/source validation for cross-frame postMessage traffic (CWE-346).
 *
 * The AI-Assistant runs as an iframe embedded on many host domains. Neither side previously
 * validated the sender of incoming `message` events, so any script or frame able to postMessage
 * could spoof host-context, feedback metadata, or widget-control events. These guards restore the
 * trust boundary without hardcoding origins that vary per embedder.
 */

/**
 * True when the event was posted by our embedding parent frame.
 *
 * Used by listeners running INSIDE the iframe. The host origin varies per embedder and cannot be
 * allowlisted here, so we validate the source window instead: the host always posts into
 * `iframe.contentWindow`, so inside the iframe legitimate messages arrive with
 * `event.source === window.parent`. When not embedded, `window.parent === window`, so self-posted
 * messages still pass. CSP `frame-ancestors` already restricts which origins may embed us, so the
 * effective trust boundary is "our configured embedders".
 */
export function isMessageFromHost(event: MessageEvent): boolean {
    return event.source === window.parent;
}

/**
 * True when the event was posted by our own iframe.
 *
 * Used by the embed script running on the HOST page. Here the legitimate sender is our iframe,
 * whose origin we know from the iframe URL, so we validate both the origin string and the exact
 * source window reference.
 */
export function isMessageFromIframe(
    event: MessageEvent,
    iframe: Pick<HTMLIFrameElement, 'contentWindow'>,
    iframeOrigin: string,
): boolean {
    // Guard against a detached iframe: `contentWindow` is null once removed from the DOM, and a
    // spoofed message can also arrive with `source === null`, so `null === null` would otherwise
    // pass the source check.
    return iframe.contentWindow !== null && event.origin === iframeOrigin && event.source === iframe.contentWindow;
}
